import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './Works.module.scss';
import WorkItem from '../WorkItem/WorkItem';
import clsx from 'clsx';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { CaseStudyModal } from '../case-study-modal';
import { setModalWork, setWorkData } from '../../redux/slices/app.slice';
import { useDispatch, useSelector } from 'react-redux';
import { TELEGRAM_CTA_URL, getTelegramCtaUrl } from '../../constants/cta';
import { WORK_CASES } from '../../constants/marketingContent';
import { trackGoal } from '../../analytics/metrika';

const TAB_ITEMS = [
  { id: 'all', label: 'Все' },
  { id: 'landing', label: 'Лендинг + WordPress' },
  { id: 'react', label: 'React + Node.js' },
  { id: 'telegram', label: 'Telegram mini app / bot' },
];

const TABS_STICKY_TOP = 64;

const Works = () => {
  const { modalWork, workData } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('all');
  const [visibleTab, setVisibleTab] = useState('all');
  const [isTabsSticky, setIsTabsSticky] = useState(false);
  const tabsShellRef = useRef(null);
  const tabsScrollRef = useRef(null);
  const gridRef = useRef(null);
  const tabRefs = useRef({});

  const isReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const isIOS = typeof navigator !== 'undefined' ? /iPad|iPhone|iPod/.test(navigator.userAgent) : false;
  const switchScrollBehavior = isReducedMotion || isIOS ? 'auto' : 'smooth';

  const visibleItems = useMemo(() => {
    if (visibleTab === 'all') {
      return WORK_CASES;
    }
    return WORK_CASES.filter((item) => item.category === visibleTab);
  }, [visibleTab]);
  const tabCounts = useMemo(() => {
    const counts = {
      all: WORK_CASES.length,
    };

    TAB_ITEMS.forEach((tab) => {
      if (tab.id === 'all') {
        return;
      }
      counts[tab.id] = WORK_CASES.filter((item) => item.category === tab.id).length;
    });

    return counts;
  }, []);

  useEffect(() => {
    const updateStickyState = () => {
      if (!tabsShellRef.current) {
        return;
      }

      const isSticky = tabsShellRef.current.getBoundingClientRect().top <= TABS_STICKY_TOP;
      setIsTabsSticky((prev) => (prev === isSticky ? prev : isSticky));
    };

    updateStickyState();
    window.addEventListener('scroll', updateStickyState, { passive: true });
    window.addEventListener('resize', updateStickyState);

    return () => {
      window.removeEventListener('scroll', updateStickyState);
      window.removeEventListener('resize', updateStickyState);
    };
  }, []);

  const handleTabClick = (tabId) => {
    trackGoal('portfolio_tab_click', { tab: tabId });

    const scrollTabIntoView = () => {
      if (!tabsScrollRef.current || !tabRefs.current[tabId]) {
        return;
      }

      if (!window.matchMedia('(max-width: 900px)').matches) {
        return;
      }

      const tabEl = tabRefs.current[tabId];
      const tabsRect = tabsScrollRef.current.getBoundingClientRect();
      const tabRect = tabEl.getBoundingClientRect();
      const isFullyVisible = tabRect.left >= tabsRect.left && tabRect.right <= tabsRect.right;

      if (isFullyVisible) {
        return;
      }

      tabEl.scrollIntoView({
        behavior: switchScrollBehavior,
        inline: 'center',
        block: 'nearest',
      });
    };

    scrollTabIntoView();

    if (gridRef.current) {
      const tabsHeight = tabsShellRef.current ? tabsShellRef.current.offsetHeight : 0;
      const scrollOffset = TABS_STICKY_TOP + tabsHeight + 12;
      const targetY = window.scrollY + gridRef.current.getBoundingClientRect().top - scrollOffset;

      if (Math.abs(window.scrollY - targetY) > 8) {
        window.scrollTo({
          top: Math.max(0, targetY),
          behavior: switchScrollBehavior,
        });
      }
    }

    if (tabId === activeTab) {
      return;
    }

    setActiveTab(tabId);
    setVisibleTab(tabId);
  };

  const closeModal = () => {
    dispatch(setModalWork(false));
  };

  const openTelegram = () => {
    trackGoal('cta_telegram_click', {
      source: 'case_modal',
      caseId: workData?.id || 'unknown',
      caseTitle: workData?.title || 'unknown',
    });

    const telegramUrl = getTelegramCtaUrl(workData?.link || '');
    window.open(telegramUrl || TELEGRAM_CTA_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <section className="container" id="works">
        <div className={clsx(styles.inner)}>
          <div className={clsx(styles.mainHead)}>
            <div className={clsx(styles.pageTitle, 'title')}>Кейсы с реальными сроками и бюджетами </div>
            <div className={clsx(styles.subtitle)}>Примеры проектов, которые можно адаптировать под вашу задачу</div>
          </div>

          <div className={clsx(styles.tabsShell, isTabsSticky && styles.tabsSticky)} ref={tabsShellRef}>
            <div className={clsx(styles.tabs)} ref={tabsScrollRef}>
              {TAB_ITEMS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={clsx(styles.tab, activeTab === tab.id && styles.tabActive)}
                  data-metrika-label={tab.label}
                  data-metrika-section="works-tabs"
                  ref={(el) => {
                    if (el) {
                      tabRefs.current[tab.id] = el;
                    }
                  }}
                  onClick={() => {
                    handleTabClick(tab.id);
                  }}>
                  <span className={styles.tabLabel}>{tab.label}</span>
                  <span className={styles.tabCount}>{tabCounts[tab.id] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>

          <div key={visibleTab} className={clsx(styles.gridTransition, styles.gridTransitionEnter)} ref={gridRef}>
            <ResponsiveMasonry columnsCountBreakPoints={{ 0: 1, 720: 2, 1080: 2 }}>
              <Masonry gutter="20px">
                {visibleItems.map((item, index) => (
                  <div key={item.id} className={clsx(styles.gridItem)}>
                    <WorkItem
                      item={item}
                      animationIndex={index}
                      hidePrice={true}
                      onClick={() => {
                        trackGoal('case_open', {
                          caseId: item.id,
                          caseCategory: item.category,
                          caseTitle: item.title,
                        });

                        dispatch(setWorkData({ ...item, hidePrice: true }));
                      }}
                    />
                  </div>
                ))}
              </Masonry>
            </ResponsiveMasonry>
            {!visibleItems.length ? <div className={clsx(styles.empty)}>В этом разделе кейсы скоро появятся</div> : null}
          </div>
        </div>
      </section>
      <Tooltip id="work-price-tooltip" place="top" className="myTooltip" />
      <CaseStudyModal isOpen={modalWork} caseData={workData} onClose={closeModal} onTelegramClick={openTelegram} />
    </>
  );
};

export default Works;
